import React, { useEffect, useState } from 'react'
import { ReportHeading } from '../common/ReportHeading'
import { Item } from '../../../common/snippets'
import { Box, Button } from '@mui/material'
import Datepicker from '../../../common/datePicker'
import DropDownOA from '../../../common/dropDownOA'
import { SplitButton } from '../../../common/SplitButton'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import moment from 'moment'
import { useAtom, useSetAtom } from 'jotai'
import { BranchBalanceReportData, fetchBranchBalanceData } from '../../../common/jotaiState/reportStateAtom'
import { removeDuplicatesFromArray } from '../../../common/helper'
import { urls } from '../../../common/constants'
import { GetRequest } from '../../../common/api'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { useSearchParams } from 'react-router-dom'
import useToken from '../../../helpers/useTokens'
import { downloadPDF } from '../../Reports/analysisReport/pdfAutotable'
import { updateAlertError } from '../../../../store/common/common_action'
import { useDispatch } from 'react-redux'
import { getDecryptedReportParams } from '../../../common/loginDecode'

const BranchBalanceReportNew = () => {
    const reportName = 'Branch Balance'
    const setAlert = useSetAtom(alertAtom)
    const [reportView, setReportView] = useState(false)
    const [fromDate, setFromDate] = useState(moment(new Date()))
    const [toDate, setToDate] = useState(moment(new Date()))
    const [branchStateData, setBranchStateData] = useState([])
    const [selBranchStateData, setSelBranchStateData] = useState('')
    const [branchCityData, setBranchCityData] = useState([])
    const [selBranchCityData, setSelBranchCityData] = useState('')
    const [branchData, setBranchData] = useState([])
    const [selBranchData, setSelBranchData] = useState('')
    const [branchBalanceFilterData] = useAtom(BranchBalanceReportData)
    const [, fetchBranchBalance] = useAtom(fetchBranchBalanceData)
    const [, setLoader] = useAtom(globalLoaderAtom)
    const [urlSearchParams] = useSearchParams()
  const searchParams = getDecryptedReportParams(urlSearchParams)
    const dispatch = useDispatch()

    let { loginData = {} } = useToken()
    let isFromUrl = false
    if (!loginData) {
        loginData = {}
        loginData.intCompanyID = searchParams.get('C') || 0
        loginData.intBranchCityID = +searchParams.get('CT') || 0
        loginData.intUserID = searchParams.get('U') || 0
        loginData.intBranchID = +searchParams.get('B') || 0
        loginData.strUserName = searchParams.get('UN') || ''
        loginData.strCompanyName = searchParams.get('CN') || ''
        loginData.strBranchName = searchParams.get('BN') || ''
        isFromUrl = true
    }
    const { intCompanyID, intBranchID, strCompanyName, intBranchCityID } = loginData

    // ---------------- STATE LIST ----------------
    useEffect(() => {
        if (branchBalanceFilterData.length > 0) {
            const stateList = [
                { StateName: "All", StateID: 0 },
                ...branchBalanceFilterData.reduce((acc, val) => {
                    if (!acc.some(s => s.StateID === val.StateID)) {
                        acc.push({ StateName: val.StateName, StateID: val.StateID });
                    }
                    return acc;
                }, [])
            ];
            setBranchStateData(stateList);
            setSelBranchStateData(stateList[0]);
        } else {
            setBranchStateData([]);
            setSelBranchStateData('');
        }
    }, [branchBalanceFilterData]);

    // ---------------- CITY LIST (depends on state) ----------------
    useEffect(() => {
        if (!selBranchStateData) return;
        const filteredCities = [
            { CityName: "All", CityID: 0 },
            ...branchBalanceFilterData
                .filter(val => selBranchStateData.StateID === 0 || val.StateID === selBranchStateData.StateID)
                .reduce((acc, val) => {
                    if (!acc.some(c => c.CityID === val.CityID)) {
                        acc.push({ CityName: val.CityName, CityID: val.CityID });
                    }
                    return acc;
                }, [])
        ];
        setBranchCityData(filteredCities);

        // Default select → login city if present in list, else first
        const loginCity = filteredCities.find(c => c.CityID === intBranchCityID);
        setSelBranchCityData(loginCity || filteredCities[0]);
    }, [selBranchStateData, branchBalanceFilterData]);

    // ---------------- BRANCH LIST (depends on city) ----------------
    // ---------------- BRANCH LIST (depends on state + city) ----------------
    useEffect(() => {
        if (!selBranchStateData || !selBranchCityData) return;

        const filteredBranches = [
            { BranchName: "All", BranchID: 0 },
            ...branchBalanceFilterData
                .filter(val => {
                    // State filter
                    const matchState =
                        selBranchStateData.StateID === 0 || val.StateID === selBranchStateData.StateID;

                    // City filter
                    const matchCity =
                        selBranchCityData.CityID === 0 || val.CityID === selBranchCityData.CityID;

                    return matchState && matchCity;
                })
                .reduce((acc, val) => {
                    if (!acc.some(b => b.BranchID === val.BranchID)) {
                        acc.push({ BranchName: val.BranchName, BranchID: val.BranchID });
                    }
                    return acc;
                }, [])
        ];

        setBranchData(filteredBranches);

        // Default select → login branch if present in list, else first
        const loginBranch = filteredBranches.find(b => b.BranchID === intBranchID);
        setSelBranchData(loginBranch || filteredBranches[0]);
    }, [selBranchStateData, selBranchCityData, branchBalanceFilterData]);

    const handleGoClick = async () => {
        if (Math.abs(toDate.diff(fromDate, 'days')) > 31) {
            dispatch(updateAlertError({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' }))
            return
        }
        try {
            const obj = {
                CompanyID: intCompanyID,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
            };
            setLoader(true)
            await fetchBranchBalance(obj, 'filterData');
        } catch (error) {
            console.error("Error fetching Branch Balance Report:", error);
        } finally {
            setLoader(false)
        }
    };

    const criteriaHeaders = [
        { field: 'fromDate', header: 'From Date', textAlign: 'left' },
        { field: 'toDate', header: 'To Date', textAlign: 'left' },
        { field: 'branchState', header: 'Branch State', textAlign: 'left' },
        { field: 'branchCity', header: 'Branch City', textAlign: 'left' },
        { field: 'branchName', header: 'Branch Name', textAlign: 'left' },
    ]

    const criteriaData = [{
        fromDate: moment(fromDate).format('DD-MMM-YYYY'),
        toDate: moment(toDate).format('DD-MMM-YYYY'),
        branchState: selBranchStateData?.StateName || '',
        branchCity: selBranchCityData?.CityName || '',
        branchName: selBranchData?.BranchName || '',
        dataType: 'entry',
    }]

    const branchBalanceColumn = [
        { headerName: 'SN', exportHead: 'SN', field: 'SN', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "20px", align: "left" },
        { headerName: 'Branch State', exportHead: 'Branch State', field: 'StateName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "100px", align: "left", isSortIcon: true, },
        { headerName: 'Branch City', exportHead: 'Branch City', field: 'CityName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "80px", align: "left", isSortIcon: true, },
        { headerName: 'Branch Name', exportHead: 'Branch Name', field: 'BranchName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "80px", align: "left", isSortIcon: true },
        { headerName: 'Opening Balance', exportHead: 'Opening Balance', field: 'OpeningBalance', xlCol: { dataType: 'number', dataFormatter: null }, minWidth: "80px", align: "right", isSortIcon: true, },
        { headerName: 'Closing Balance', exportHead: 'Closing Balance', field: 'ClosingBalance', xlCol: { dataType: 'number', dataFormatter: null }, minWidth: "80px", align: "right", isSortIcon: true, },
    ]

    const getMainHeader = (columns = []) =>
        columns.map(col => {
            const header = col.headerName || col.header || ''
            return {
                ...col,
                header,
                exportHead: col.exportHead || header,
                align: col.align,
                weight: 80,
                dataType: 'header',
            }
        })

    const generateReport = async (isPdf = false) => {
        try {
            const diffDays = Math.abs(toDate.diff(fromDate, 'days'))
            const isAllSelected = selBranchStateData?.StateID === 0 || selBranchCityData?.CityID === 0 || selBranchData?.BranchID === 0

            if (isAllSelected && diffDays > 15) {
                dispatch(updateAlertError({ show: true, msg: 'For All selection, date range cannot exceed 15 days', nextId: '' }))
                return
            }
            if (!isAllSelected && diffDays > 31) {
                dispatch(updateAlertError({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' }))
                return
            }
            const obj = {
                CompanyID: intCompanyID,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
                intStateID: selBranchStateData.StateID,
                intCityID: selBranchCityData.CityID,
                intBranchID: selBranchData.BranchID,
            }
            setLoader(true)
            const res = await GetRequest(urls.getBranchBalanceReport + '?', obj)
            setLoader(false)

            if (res?.status !== 'Success' || !res.Data.table?.length) {
                setAlert({ show: true, msg: 'No data found' })
                return
            }

            const tableData = res.Data.table.map((val, i) => ({ ...val, SN: i + 1, dataType: 'entry' }))

            const reportPayload = {
                reportName: `${reportName} Report`,
                criteriaHeaders,
                criteriaData,
                mainHeader: getMainHeader(branchBalanceColumn),
                tableData,
                serialNokey: 'SN',
            }
            if (isPdf === true) {
                downloadPDF({
                    reportName: `${reportName} Report`,
                    criteriaHeaders,
                    criteriaData,
                    tableData,
                    visibleColumns: getMainHeader(branchBalanceColumn),
                    loginData,
                    hideHeader: false
                })
                return
            }
            const rid = Date.now().toString()
            if (!window.generatedReports) window.generatedReports = {}
            window.generatedReports[rid] = reportPayload
            window.open(`/view-report?rid=${rid}`, '_blank')

        } catch (error) {
            console.error('Error fetching Branch Balance Report:', error)
            setLoader(false)
        }
    }

    return (
        <>
            <Item sx={{ padding: '0' }} className="reports">
                {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
                {!isFromUrl && <ReportHeading reportName={reportName} />}
                <Box sx={{ ...sx.reportFlex, justifyContent: 'start', gap: isFromUrl ? '15px' : '10px' }}>
                    {!isFromUrl && <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id="selectCompnay"
                            label="Selected Company"
                            defaultvalue={strCompanyName}
                            disabled={true}
                            width="100%"
                        />
                    </Item>}
                    <Item sx={isFromUrl ? sx.itemsxGrow : sx.itemsx} className="input--container bookingDatePicker">
                        <Datepicker
                            label={`From Date`}
                            name={'from_date'}
                            id={'fromDate'}
                            value={fromDate}
                            dateChange={(date) => {
                                setFromDate(date)
                                setReportView(false)
                            }}
                            width={isFromUrl ? '100%' : '300px'}
                        />
                    </Item>
                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '10px', width: isFromUrl ? '100%' : 'auto' }}>
                        <Item sx={isFromUrl ? sx.itemsxGrow : sx.itemsx} className="input--container bookingDatePicker">
                            <Datepicker
                                label={'To Date'}
                                name={'to_date'}
                                id={'toDate'}
                                value={toDate}
                                dateChange={(date) => {
                                    setToDate(date)
                                    setReportView(false)
                                }}
                                width={isFromUrl ? '100%' : '300px'}
                            />
                        </Item>
                        <Item sx={sx.itemsx}>
                            <Button
                                id="branchBalanceGoBtn"
                                variant="contained"
                                className="go click-button"
                                onClick={handleGoClick}
                            >
                                GO
                            </Button>
                        </Item>
                    </Box>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            label={`Branch State`}
                            id={'branchState'}
                            searchPlaceholder={`Search By Branch State`}
                            dataKey={`StateName`}
                            idKey={`StateID`}
                            data={removeDuplicatesFromArray(branchStateData, 'StateID') || []}
                            value={selBranchStateData?.StateName || ''}
                            defaultvalue={selBranchStateData?.StateName || ''}
                            onSelect={(obj) => { setSelBranchStateData(obj); setReportView(false) }}
                            width="100%"
                            nextId={'selectedBranch'}
                            prevId={'goButtonDate'}
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id={'brnachCity'}
                            label={'Branch City'}
                            searchPlaceholder={`Search By Branch City`}
                            data={removeDuplicatesFromArray(branchCityData, 'CityID') || []}
                            defaultvalue={selBranchCityData?.CityName || ''}
                            onSelect={(val) => { setSelBranchCityData(val); setReportView(false) }}
                            dataKey={`CityName`}
                            idKey={`CityID`}
                            nextId={'generateButton'}
                            prevId={'paymentType'}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id={'selectedBranch'}
                            label={'Branch Name'}
                            searchPlaceholder={`Search By Branch`}
                            data={removeDuplicatesFromArray(branchData, 'BranchID') || []}
                            defaultvalue={selBranchData?.BranchName || ''}
                            onSelect={(val) => { setSelBranchData(val); setReportView(false) }}
                            dataKey={`BranchName`}
                            idKey={`BranchID`}
                            nextId={'generateButton'}
                            prevId={'paymentType'}
                            width="100%"
                        />
                    </Item>
                    {isFromUrl ?
                        <Button variant="contained" sx={{ width: '100%', height: '40px' }} className="btn btn-primary" onClick={() => generateReport(true)} id1={'generateButton'}>
                            DOWNLOAD PDF
                        </Button> :
                        <>
                            <Item sx={sx.itemsx}>
                                <SplitButton
                                    options={['GENERATE NEW FORMAT', 'GENERATE OLD FORMAT']}
                                    id1={'generateButton'}
                                    onClick1={generateReport}
                                />
                            </Item>
                        </>}
                </Box>
            </Item>
        </>
    )
}

export default BranchBalanceReportNew
